import { KiteProgressIndicator } from '@kite/react-kite';
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAnalytics } from 'hooks/useAnalytics';
import { useAuthSession } from 'hooks/useAuthSession';
import { useAuthUser } from 'hooks/useAuthUser';
import { useRouteUtils } from 'hooks/useRouteUtils';
import { IRoutePageInfo } from 'types/common';
import { UserCapabilityName } from 'types/userCapabilityName';
import { getPageName } from './PageRouteUtils';

/**
 * List of all the Base routes on the Left Hand Nav
 * This is the list we walk the user through if they don't have permissions.
 */
const baseRoutePaths = ['/home', '/feature-toggles', '/no-access'];

/**
 * By default, we send a user to the dashboard page (top most page in the baseRoutePaths list).
 * But if they don't have permissions or don't have the services we redirect them to the next
 * page on the list and try that one.
 * @param currentRoute the current path they are on
 */
const getNextRoute = (currentRoute: string): string => {
  let index = baseRoutePaths.findIndex((basePath) =>
    currentRoute.startsWith(basePath)
  );
  if (index === -1) {
    index = 0;
  }

  // try next page
  index += 1;
  return baseRoutePaths[index];
};

interface IProtectedRouteProps {
  userCapabilities?: UserCapabilityName | UserCapabilityName[];
  pageInfo?: IRoutePageInfo;
  children: any;
}

const ProtectedRoute = ({
  userCapabilities,
  pageInfo,
  children,
}: IProtectedRouteProps) => {
  const { isAuthenticating, isAuthenticated, isAuthError } = useAuthSession();
  const { authUser, isError: loadUserError } = useAuthUser();
  const { pathname, search } = useLocation();
  const { checkRoutePermissions } = useRouteUtils();
  const { trackPageView } = useAnalytics();

  const hasPermission = checkRoutePermissions({
    userCapabilities,
  });

  useEffect(() => {
    if (!pageInfo || !authUser || !hasPermission) {
      return;
    }

    let { siteSection } = pageInfo;
    const { siteSubSection, siteSubSubSection, skipPageView } = pageInfo;

    const pageName = getPageName(pageInfo);
    siteSection = siteSection ?? pageName;

    if (!skipPageView && siteSection) {
      trackPageView(siteSection, siteSubSection, siteSubSubSection);
    }
    // omit trackPageView as deps
  }, [pageInfo, authUser]);

  if (isAuthError || loadUserError) {
    return <Navigate to="/500" />;
  }

  if (isAuthenticating) {
    return (
      <div className="display-loader">
        <KiteProgressIndicator id="kp1" />
      </div>
    );
  }

  // user needs a value auth code.
  if (!isAuthenticated) {
    return <Navigate to="/signed-out" />;
  }

  // user has an auth code but the auth user object isn't built yet.
  if (isAuthenticated && !authUser) {
    return (
      <div className="display-loader">
        <KiteProgressIndicator id="kp1" />
      </div>
    );
  }

  if (!hasPermission) {
    return <Navigate to={getNextRoute(pathname) + search} />;
  }

  return children;
};

export default ProtectedRoute;
